import { DiscoveryApi, FetchApi, createApiRef } from '@backstage/core-plugin-api';
import { Specreq } from '@infra360/plugin-specreq-common';

// 1. Define the API Reference here (The "Key")
export const specreqApiRef = createApiRef<SpecreqApi>({
  id: 'plugin.specreq.service',
});

export interface Domain {
  id: string;
  title: string;
}

export interface SpecreqApi {
  listRequests(): Promise<Specreq[]>;
  createRequest(request: Partial<Specreq>): Promise<Specreq>;
  getRequest(id: string): Promise<Specreq>;
  updateRequest(id: string, patch: Partial<Specreq>): Promise<Specreq>;
  deleteRequest(id: string): Promise<void>;
  getDomains(): Promise<Domain[]>;
}

export class SpecreqClient implements SpecreqApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('specreq');
  }

  async listRequests(): Promise<Specreq[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch requests: ${response.statusText}`);
    }
    return response.json();
  }

  async createRequest(request: Partial<Specreq>): Promise<Specreq> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      throw new Error(`Failed to create request: ${response.statusText}`);
    }
    return response.json();
  }

  async getRequest(id: string): Promise<Specreq> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests/${id}`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch request ${id}: ${response.statusText}`);
    }
    return response.json();
  }

  async updateRequest(id: string, patch: Partial<Specreq>): Promise<Specreq> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    });

    if (!response.ok) {
      throw new Error(`Failed to update request: ${response.statusText}`);
    }
    return response.json();
  }

  async deleteRequest(id: string): Promise<void> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/requests/${id}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      throw new Error(`Failed to delete request: ${response.statusText}`);
    }
  }

  async getDomains(): Promise<Domain[]> {
    const baseUrl = await this.getBaseUrl();
    const response = await this.fetchApi.fetch(`${baseUrl}/domains`);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch domains: ${response.statusText}`);
    }
    return response.json();
  }
}